import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || '';

function StatusChip({ status }) {
  const color = status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'warning';
  return <Chip size="small" label={status} color={color} />;
}

export default function StaffingLeaveReviewPage({ mode = 'leave' }) {
  const { token } = useParams();
  const apiBase = mode === 'outdoor' ? 'staffing-outdoor-review' : 'staffing-leave-review';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/public/${apiBase}/${token}`);
      const json = await res.json().catch(() => ({}));
      if (res.status === 410) {
        setError(json.error || 'This link has expired.');
        return;
      }
      if (!res.ok || !json.success) {
        setError(json.error || 'Could not load this request.');
        return;
      }
      setData(json);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, apiBase]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const submitAction = async (action) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/public/${apiBase}/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reviewer_notes: notes })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setError(json.error || 'Action failed.');
        return;
      }
      setDone(json.request?.status || action);
      await load();
    } catch {
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography>Loading request…</Typography>
      </Container>
    );
  }

  if (error && !data) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const req = data?.request;
  const isPending = req?.status === 'pending' && !done;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight={800}>
          {mode === 'outdoor' ? 'Outdoor duty review' : 'Leave request review'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {req?.company_name} — Manager: {req?.manager_name || '—'}
        </Typography>

        {done && (
          <Alert severity="success">
            Request {done === 'approved' ? 'approved' : 'rejected'} successfully.
          </Alert>
        )}
        {error && <Alert severity="warning">{error}</Alert>}

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {req?.candidate_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[req?.candidate_email, req?.candidate_mobile].filter(Boolean).join(' · ')}
            </Typography>
            {mode === 'leave' ? (
              <Stack spacing={0.5} sx={{ mt: 2 }}>
                <Typography><strong>Type:</strong> {req?.leave_type}</Typography>
                <Typography><strong>Dates:</strong> {req?.from_date} → {req?.to_date} ({req?.days} day(s))</Typography>
                <Typography><strong>Reason:</strong> {req?.reason || '—'}</Typography>
                <StatusChip status={req?.status} />
              </Stack>
            ) : (
              <Stack spacing={0.5} sx={{ mt: 2 }}>
                <Typography><strong>Dates:</strong> {req?.from_date} → {req?.to_date} ({req?.days} day(s))</Typography>
                <Typography><strong>Location:</strong> {req?.location || '—'}</Typography>
                <Typography><strong>Purpose:</strong> {req?.purpose || '—'}</Typography>
                <StatusChip status={req?.status} />
              </Stack>
            )}

            {mode === 'leave' && data?.balances?.balances && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>Leave balance ({data.balances.year})</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {['casual', 'sick', 'other'].map((t) => (
                    <Chip
                      key={t}
                      label={`${t}: ${data.balances.balances[t]?.available ?? 0} available / ${data.balances.balances[t]?.quota ?? 0} quota`}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Candidate leave history
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.leaveHistory || []).slice(0, 12).map((r) => (
                  <TableRow key={`l-${r.id}`}>
                    <TableCell>{r.leave_type}</TableCell>
                    <TableCell>{r.from_date}</TableCell>
                    <TableCell>{r.to_date}</TableCell>
                    <TableCell>{r.days}</TableCell>
                    <TableCell><StatusChip status={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Other pending leave (your team)
            </Typography>
            {(data?.pendingLeave || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">None</Typography>
            ) : (
              (data?.pendingLeave || []).map((p) => (
                <Typography key={p.id} variant="body2">
                  {p.candidate_name} — {p.leave_type} {p.from_date} to {p.to_date} ({p.days}d)
                </Typography>
              ))
            )}
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }} gutterBottom>
              Other pending outdoor duty
            </Typography>
            {(data?.pendingOutdoor || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">None</Typography>
            ) : (
              (data?.pendingOutdoor || []).map((p) => (
                <Typography key={p.id} variant="body2">
                  {p.candidate_name} — {p.from_date} to {p.to_date} {p.location ? `@ ${p.location}` : ''}
                </Typography>
              ))
            )}
          </CardContent>
        </Card>

        {isPending && (
          <Card variant="outlined">
            <CardContent>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes for employee (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={2}>
                <Button variant="contained" color="success" disabled={submitting} onClick={() => submitAction('approve')}>
                  Approve
                </Button>
                <Button variant="outlined" color="error" disabled={submitting} onClick={() => submitAction('reject')}>
                  Reject
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
